import { useCallback, useMemo, useState } from 'react';

import { GX_API_URL } from '@/constants/env';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import type {
  ExportMetaContextType,
  ExportPreviewContextType,
  ExportRequestContextType,
  ExportSettingsContextType,
  ExportSettingsType
} from '@/features/DataHub/modules/Export/types';
import { defaultExportSettings, ExportTypes } from '@/features/DataHub/modules/Export/types';
import { useAppSelector } from '@/hooks/redux-hooks';
import copyToClipboard from '@/utils/copyToClipboard';

import useExportAnalytics from './useExportAnalytics';
import useExportApiStrings from './useExportApiStrings';
import useExportMutation from './useExportMutation';
import useExportName from './useExportName';

export type UseDataHubExportReturn = {
  settings: ExportSettingsContextType;
  preview: ExportPreviewContextType;
  request: ExportRequestContextType;
  meta: ExportMetaContextType;
};

const useDataHubExport = (): UseDataHubExportReturn => {
  // State
  const exportSliceType = useAppSelector(state => state.export.type);
  const [exportSettings, setExportSettings] = useState<ExportSettingsType>({
    ...defaultExportSettings,
    exportType:
      exportSliceType && exportSliceType === ExportTypes.DATA ? ExportTypes.DATA : ExportTypes.IMAGE
  });
  const [previewOptionId, setPreviewOptionId] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewDownloadFn, setPreviewDownloadFn] = useState<(() => void) | null>(null);
  const [previewContentToCopy, setPreviewContentToCopy] = useState<string | null>(null);

  // Selectors
  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const { userConfig } = useAppSelector(state => state.userConfig);
  const userTimezone = userConfig.timezone.value;
  const userDateFormat = userConfig.dateFormat.value;

  // Derived
  const uniqueSymbols = useMemo(() => new Set(symbols), [symbols]);
  const exportOption = previewOptionId ? (optionById.get(previewOptionId) ?? null) : null;

  // Composed hooks
  const { exportName, setExportName, createExportName } = useExportName();
  const { capturePosthogEvent } = useExportAnalytics(exportOption, exportSettings);
  const { exportApiRequestString, exportCurlRequestString } = useExportApiStrings({
    symbols,
    exportSettings,
    userTimezone,
    gxURL: GX_API_URL
  });
  const { exportDataMutation, loading, exportStatus, setExportStatus, resetExportStatus } =
    useExportMutation({
      exportName,
      exportSettings,
      symbols,
      userTimezone,
      userDateFormat,
      createExportName,
      setExportName,
      setPreviewOptionId
    });

  // Handlers
  const updateExportSetting = useCallback(
    <K extends keyof ExportSettingsType>(key: K, value: ExportSettingsType[K]) => {
      setExportSettings(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleCopyToClipBoard = useCallback(
    (textToCopy: string) => {
      capturePosthogEvent();
      copyToClipboard(textToCopy, 'Copied to clipboard!');
    },
    [capturePosthogEvent]
  );

  const handleExportData = useCallback(async () => {
    if (loading) return;
    capturePosthogEvent();
    if (previewOptionId) {
      return exportDataMutation({ type: previewOptionId });
    }
  }, [capturePosthogEvent, exportDataMutation, previewOptionId, loading]);

  const resetExportState = useCallback(() => {
    setExportSettings({
      ...defaultExportSettings,
      exportType:
        exportSliceType && exportSliceType === ExportTypes.DATA
          ? ExportTypes.DATA
          : ExportTypes.IMAGE
    });
    setPreviewOptionId(null);
    setShowSuccessScreen(false);
    setExportName(createExportName());
  }, [createExportName, exportSliceType, setExportName]);

  // Structured return - each object matches its context type
  const settings: ExportSettingsContextType = useMemo(
    () => ({ exportSettings, setExportSettings, updateExportSetting }),
    [exportSettings, updateExportSetting]
  );

  const preview: ExportPreviewContextType = useMemo(
    () => ({
      previewOptionId,
      setPreviewOptionId,
      previewLoading,
      setPreviewLoading,
      previewDownloadFn,
      setPreviewDownloadFn,
      previewContentToCopy,
      setPreviewContentToCopy
    }),
    [previewOptionId, previewLoading, previewDownloadFn, previewContentToCopy]
  );

  const request: ExportRequestContextType = useMemo(
    () => ({
      exportDataWithParams: exportDataMutation,
      loading,
      showSuccessScreen,
      setShowSuccessScreen,
      exportStatus,
      setExportStatus,
      resetExportStatus,
      handleCopyToClipBoard,
      handleExportData,
      capturePosthogEvent,
      resetExportState
    }),
    [
      exportDataMutation,
      loading,
      showSuccessScreen,
      exportStatus,
      setExportStatus,
      resetExportStatus,
      handleCopyToClipBoard,
      handleExportData,
      capturePosthogEvent,
      resetExportState
    ]
  );

  const meta: ExportMetaContextType = useMemo(
    () => ({
      exportName,
      setExportName,
      uniqueSymbols,
      exportApiRequestString,
      exportCurlRequestString,
      timezone: userTimezone
    }),
    [
      exportName,
      setExportName,
      uniqueSymbols,
      exportApiRequestString,
      exportCurlRequestString,
      userTimezone
    ]
  );

  return { settings, preview, request, meta };
};

export default useDataHubExport;
